import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { SxProps, Theme } from "@mui/material";
import type { FC, KeyboardEvent, MouseEvent } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { MOTION } from "@/theme/tokens";
import { GlyphButton } from "@/ui/Clickable/GlyphButton";
import type { DropdownRowHint as DropdownRowHintSpec } from "./types";

/** The glyph's mark (its `data-dropdown-row-hint`), so its row can reveal it on hover (`dropdownRowHintRevealSx`). */
const HINT_SELECTOR = "[data-dropdown-row-hint]";

/**
 * The row's half of the reveal, spread into the row's `sx`: pointing at the row shows its glyph. A plain selector, not a
 * hover media query: the row's own hover wash sits under that query key, which a second one would overwrite. A touch
 * screen, where nothing hovers, shows the glyph at rest instead (the glyph's own `sx`).
 */
export const dropdownRowHintRevealSx = { [`&:hover ${HINT_SELECTOR}`]: { opacity: 1 } } as const;

const HINT_SX: SxProps<Theme> = {
	opacity: 0,
	transition: `opacity ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}, background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
	"&:focus-visible, &[data-revealed='true']": { opacity: 1 },
	"@media (hover: none)": { opacity: 1 },
	"@media (prefers-reduced-motion: reduce)": { transition: "none" },
};

// The glyph explains its row and never picks it: the click, and Enter or Space on the focused glyph, stop here.
const keepRowUnpicked = (event: MouseEvent<HTMLButtonElement>) => event.stopPropagation();
const keepFocusInPanel = (event: MouseEvent<HTMLButtonElement>) => {
	// A press keeps focus where it was (the panel's search), as the group headers' chevrons do.
	event.preventDefault();
	event.stopPropagation();
};
const keepKeysOffRow = (event: KeyboardEvent<HTMLButtonElement>) => {
	if (event.key !== "Enter" && event.key !== " ") return;
	event.preventDefault();
	event.stopPropagation();
};

/**
 * Beside the menu when the viewport has room on its far side; otherwise above (or below) the glyph. Never on the near
 * side, which is the row's own label: MUI's own fallback for `right` is `left`, over the name being explained.
 */
const HINT_POPPER_PROPS = { modifiers: [{ name: "flip", options: { fallbackPlacements: ["top", "bottom"] } }] };

type Props = {
	hint: DropdownRowHintSpec;
	/** The row is the keyboard's: show the glyph as hovering the row would. */
	revealed: boolean;
};

/**
 * `DropdownItem.hint`: an info glyph at the row's trailing edge whose tooltip explains the option. `enterTouchDelay` 0:
 * a touch screen has no hover, so a tap opens the explanation at once rather than after MUI's long press.
 */
export const DropdownRowHint: FC<Props> = ({ hint, revealed }) => (
	<InHouseTooltip title={hint.text} describeChild placement="right" enterTouchDelay={0} slotProps={{ popper: HINT_POPPER_PROPS }}>
		<GlyphButton
			data-dropdown-row-hint
			icon={<InfoOutlinedIcon />}
			ariaLabel={hint.ariaLabel}
			dataTestId={hint.dataTestId}
			data-revealed={revealed}
			onClick={keepRowUnpicked}
			onMouseDown={keepFocusInPanel}
			onKeyDown={keepKeysOffRow}
			sx={HINT_SX}
		/>
	</InHouseTooltip>
);
